/**
 * Sync queue -- IndexedDB-backed queue of pending field-app mutations.
 *
 * Module 2.7 Phase 3 Part 1.
 *
 * Why this exists:
 *   When a worker authors a field report on a phone with patchy site
 *   coverage, the create call may fail before we even get a 4xx. We
 *   want the UI to feel "done" -- the report appears in the list,
 *   the queue length ticks up by one -- and we want the request to
 *   replay automatically the moment connectivity returns.
 *
 * Storage:
 *   One IndexedDB database (BM_FIELD_SYNC) with one object store
 *   (pending_mutations). Each entry has a numeric autoIncrement id,
 *   the axios-shaped request, a small ``kind`` tag for the badge UX,
 *   created_at + last_attempt_at timestamps, and an attempts counter
 *   that the replayer uses to back off.
 *
 * Concurrency:
 *   Single tab is the common case for the field PWA; multi-tab is
 *   tolerated -- the replayer skips an entry while another worker is
 *   processing it (in-memory ``_processing`` set scoped per tab).
 *
 * Browser support:
 *   IndexedDB is available everywhere we ship (PWA + Android Chrome
 *   + iOS Safari 14+). The module guards every call with a
 *   ``isSupported()`` check so SSR / unit-tests without IDB still
 *   import cleanly.
 */


export type SyncQueueKind =
  | 'field.report.create'
  | 'field.report.update'
  | 'field.report.submit'
  | 'field.report.approve'
  | 'field.report.reject';


export interface SyncQueueRequest {
  /** Relative path under the API base URL, e.g. ``/field/reports``. */
  url: string;
  method: 'POST' | 'PATCH' | 'DELETE' | 'PUT';
  body?: unknown;
}


export type SyncQueueStatus = 'pending' | 'failed';


export interface SyncQueueEntry {
  id: number;
  kind: SyncQueueKind;
  /**
   * Short human label shown in the pending-sync badge tooltip
   * (e.g. ``"Report #FR-2026-014"``). Optional -- callers that
   * can't synthesise a label drop this.
   */
  label?: string;
  request: SyncQueueRequest;
  createdAt: number;        // unix ms
  lastAttemptAt: number | null;
  attempts: number;
  lastError: string | null;
  /**
   * "pending": eligible for the next replay round.
   * "failed":  the server returned a non-retryable error (typically
   *            4xx / 409) so the replayer parked it for the user to
   *            review on /sync/pending. The entry stays in the queue
   *            until the user discards or retries it.
   * Added in Phase 3 Part 2 (DB version 2). Entries written under
   * version 1 are migrated to ``pending`` on first open.
   */
  status: SyncQueueStatus;
  /** HTTP status code that produced the failure (server-wins / 4xx). */
  failureCode: number | null;
  /** Human-readable failure detail (``detail`` from the server). */
  failureDetail: string | null;
  /** When the entry transitioned to failed -- unix ms. */
  failedAt: number | null;
}


export type SyncQueueEntryDraft = Omit<
  SyncQueueEntry,
  'id' | 'createdAt' | 'lastAttemptAt' | 'attempts' | 'lastError'
  | 'status' | 'failureCode' | 'failureDetail' | 'failedAt'
>;


const DB_NAME = 'BM_FIELD_SYNC';
const STORE_NAME = 'pending_mutations';
// v2 (Phase 3 Part 2): adds status / failureCode / failureDetail /
// failedAt columns on existing rows.
const DB_VERSION = 2;


type SubscribeFn = (count: number) => void;
const _subscribers = new Set<SubscribeFn>();
const _processing = new Set<number>();


function isSupported(): boolean {
  return typeof indexedDB !== 'undefined';
}


function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (!isSupported()) {
      reject(new Error('IndexedDB is not available in this environment'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (event) => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, {
          keyPath: 'id',
          autoIncrement: true,
        });
        store.createIndex('by_created_at', 'createdAt', {
          unique: false,
        });
        store.createIndex('by_status', 'status', { unique: false });
        return;
      }

      // v1 -> v2: backfill the failure-state fields onto existing
      // rows so a user who queued reports under v1 keeps them.
      const oldVersion = (event as IDBVersionChangeEvent).oldVersion;
      if (oldVersion < 2) {
        const tx = req.transaction!;
        const store = tx.objectStore(STORE_NAME);
        if (!store.indexNames.contains('by_status')) {
          store.createIndex('by_status', 'status', { unique: false });
        }
        const cursorReq = store.openCursor();
        cursorReq.onsuccess = () => {
          const cursor = cursorReq.result;
          if (!cursor) return;
          const v = cursor.value as SyncQueueEntry;
          if (v.status === undefined) {
            v.status = 'pending';
            v.failureCode = null;
            v.failureDetail = null;
            v.failedAt = null;
            cursor.update(v);
          }
          cursor.continue();
        };
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}


async function _withStore<T>(
  mode: IDBTransactionMode,
  cb: (store: IDBObjectStore) => Promise<T> | T,
): Promise<T> {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, mode);
    const store = tx.objectStore(STORE_NAME);
    let result: T;
    Promise.resolve(cb(store))
      .then((r) => { result = r; })
      .catch(reject);
    tx.oncomplete = () => {
      db.close();
      resolve(result);
    };
    tx.onabort = () => {
      db.close();
      reject(tx.error || new Error('IndexedDB transaction aborted'));
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error);
    };
  });
}


function _idbReq<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}


function _notify(count: number) {
  for (const fn of _subscribers) {
    try { fn(count); } catch { /* subscriber bug -- ignore */ }
  }
}


export const syncQueue = {
  isSupported,

  /** Add a new pending mutation. Resolves to the assigned id. */
  async enqueue(draft: SyncQueueEntryDraft): Promise<number> {
    if (!isSupported()) {
      throw new Error('IndexedDB unavailable; cannot enqueue');
    }
    const now = Date.now();
    const entry: Omit<SyncQueueEntry, 'id'> = {
      kind: draft.kind,
      label: draft.label,
      request: draft.request,
      createdAt: now,
      lastAttemptAt: null,
      attempts: 0,
      lastError: null,
      status: 'pending',
      failureCode: null,
      failureDetail: null,
      failedAt: null,
    };
    const id = await _withStore('readwrite', async (store) => {
      const r = store.add(entry as unknown as SyncQueueEntry);
      const k = await _idbReq(r);
      return Number(k);
    });
    const c = await this.count();
    _notify(c);
    return id;
  },

  /** Return all entries sorted by createdAt asc (FIFO replay order). */
  async getAll(): Promise<SyncQueueEntry[]> {
    if (!isSupported()) return [];
    return _withStore('readonly', async (store) => {
      const r = store.index('by_created_at').getAll();
      return _idbReq(r);
    });
  },

  /** Pending entry count -- the value the badge renders. */
  async count(): Promise<number> {
    if (!isSupported()) return 0;
    return _withStore('readonly', async (store) => {
      return _idbReq(store.count());
    });
  },

  /** Remove an entry (replay succeeded, or operator-driven discard). */
  async remove(id: number): Promise<void> {
    if (!isSupported()) return;
    await _withStore('readwrite', async (store) => {
      await _idbReq(store.delete(id));
    });
    const c = await this.count();
    _notify(c);
  },

  /** Update attempt bookkeeping after a failed replay. */
  async recordAttempt(
    id: number,
    args: { error: string },
  ): Promise<void> {
    if (!isSupported()) return;
    await _withStore('readwrite', async (store) => {
      const existing = await _idbReq(store.get(id));
      if (!existing) return;
      const e = existing as SyncQueueEntry;
      e.attempts += 1;
      e.lastAttemptAt = Date.now();
      e.lastError = args.error.slice(0, 500);
      await _idbReq(store.put(e));
    });
    const c = await this.count();
    _notify(c);
  },

  /**
   * Park an entry as ``failed`` -- the replayer parks 4xx / 409
   * responses here so the user can review them on /sync/pending
   * rather than have them silently dropped.
   *
   * Server-wins for non-photo fields: a 409 PATCH on a report the
   * consultant already updated stays in the failed bucket so the
   * author can see what was lost. Photo appends never conflict.
   */
  async markFailed(
    id: number,
    args: { code: number; detail: string },
  ): Promise<void> {
    if (!isSupported()) return;
    await _withStore('readwrite', async (store) => {
      const existing = await _idbReq(store.get(id));
      if (!existing) return;
      const e = existing as SyncQueueEntry;
      e.status = 'failed';
      e.failureCode = args.code;
      e.failureDetail = args.detail.slice(0, 500);
      e.failedAt = Date.now();
      e.lastAttemptAt = Date.now();
      e.attempts += 1;
      e.lastError = `${args.code}: ${args.detail}`.slice(0, 500);
      await _idbReq(store.put(e));
    });
    const c = await this.count();
    _notify(c);
  },

  /** Move a ``failed`` entry back to ``pending`` for one more try. */
  async markPending(id: number): Promise<void> {
    if (!isSupported()) return;
    await _withStore('readwrite', async (store) => {
      const existing = await _idbReq(store.get(id));
      if (!existing) return;
      const e = existing as SyncQueueEntry;
      e.status = 'pending';
      e.failureCode = null;
      e.failureDetail = null;
      e.failedAt = null;
      e.lastError = null;
      await _idbReq(store.put(e));
    });
    const c = await this.count();
    _notify(c);
  },

  /** Entries filtered by status, sorted FIFO. */
  async getByStatus(status: SyncQueueStatus): Promise<SyncQueueEntry[]> {
    if (!isSupported()) return [];
    return _withStore('readonly', async (store) => {
      const all = await _idbReq(store.index('by_created_at').getAll());
      return (all as SyncQueueEntry[]).filter((e) => e.status === status);
    });
  },

  /** Wipe everything -- developer / test helper, not exposed in UI. */
  async clear(): Promise<void> {
    if (!isSupported()) return;
    await _withStore('readwrite', async (store) => {
      await _idbReq(store.clear());
    });
    _notify(0);
  },

  /**
   * Subscribe to count changes. The callback fires after every
   * enqueue / remove / clear in this tab. Returns an unsubscribe
   * function. The current count is delivered on subscribe so
   * components don't render with a stale zero.
   */
  subscribe(fn: SubscribeFn): () => void {
    _subscribers.add(fn);
    this.count().then(fn).catch(() => fn(0));
    return () => { _subscribers.delete(fn); };
  },

  /** Mark an entry as "currently being replayed in this tab". */
  beginProcessing(id: number): boolean {
    if (_processing.has(id)) return false;
    _processing.add(id);
    return true;
  },

  endProcessing(id: number): void {
    _processing.delete(id);
  },
};


/**
 * Heuristic for "is this error worth retrying?".
 *
 * We retry network failures, CORS / DNS aborts, 408 / 429, and any
 * 5xx. We DON'T retry 4xx other than 408 / 429 -- a 400 / 403 / 404
 * will fail forever, and retrying would just spam the server. The
 * UI surfaces those as hard failures via the toast.
 */
export function isRetryableSyncError(err: unknown): boolean {
  if (err == null) return false;
  const e = err as {
    response?: { status?: number };
    code?: string;
    message?: string;
    isAxiosError?: boolean;
  };

  // Genuine network failure -- no response object at all.
  if (e.isAxiosError && !e.response) return true;
  if (e.code === 'ERR_NETWORK' || e.code === 'ECONNABORTED') return true;
  if (typeof e.message === 'string' && /network/i.test(e.message)) return true;

  const status = e.response?.status ?? 0;
  if (status === 408 || status === 429) return true;
  if (status >= 500 && status < 600) return true;
  return false;
}
